import type { TranslationKey } from '~/shared/internationalization/translation';

export const installationGuidePageRoute = {
    patterns: {
        installationGuide: '/installation-guide',
        installationGuideWithId: '/installation-guide/:jobArchitectureId',
    },
} as const;

export const TASK_IDS = [
    'define-purpose',
    'company-information',
    'data-upload',
    'build-foundation',
    'review-families-clusters',
    'create-job-profile-template',
    'generate-first-roles',
    'review-publish-roles',
    'invite-reviewers',
    'select-career-framework',
    'generate-first-levels',
    'review-publish-levels',
    'configure-skill-settings',
    'review-publish-skills',
] as const;

export type TaskId = (typeof TASK_IDS)[number];

export const taskIdToTitleKey: Record<TaskId, TranslationKey> = {
    'define-purpose': 'pages.installation-guide.tasks.define-purpose',
    'company-information': 'pages.installation-guide.tasks.company-information',
    'data-upload': 'pages.installation-guide.tasks.data-upload',
    'build-foundation': 'pages.installation-guide.tasks.build-foundation',
    'review-families-clusters': 'pages.installation-guide.tasks.review-families-clusters',
    'create-job-profile-template': 'pages.installation-guide.tasks.create-job-profile-template',
    'generate-first-roles': 'pages.installation-guide.tasks.generate-first-roles',
    'review-publish-roles': 'pages.installation-guide.tasks.review-publish-roles',
    'invite-reviewers': 'pages.installation-guide.tasks.invite-reviewers',
    'select-career-framework': 'pages.installation-guide.tasks.select-career-framework',
    'generate-first-levels': 'pages.installation-guide.tasks.generate-first-levels',
    'review-publish-levels': 'pages.installation-guide.tasks.review-publish-levels',
    'configure-skill-settings': 'pages.installation-guide.tasks.configure-skill-settings',
    'review-publish-skills': 'pages.installation-guide.tasks.review-publish-skills',
};

export const taskIdToDescriptionKey: Record<TaskId, TranslationKey> = {
    'define-purpose': 'pages.installation-guide.tasks.descriptions.define-purpose',
    'company-information': 'pages.installation-guide.tasks.descriptions.company-information',
    'data-upload': 'pages.installation-guide.tasks.descriptions.data-upload',
    'build-foundation': 'pages.installation-guide.tasks.descriptions.build-foundation',
    'review-families-clusters': 'pages.installation-guide.tasks.descriptions.review-families-clusters',
    'create-job-profile-template': 'pages.installation-guide.tasks.descriptions.create-job-profile-template',
    'generate-first-roles': 'pages.installation-guide.tasks.descriptions.generate-first-roles',
    'review-publish-roles': 'pages.installation-guide.tasks.descriptions.review-publish-roles',
    'invite-reviewers': 'pages.installation-guide.tasks.descriptions.invite-reviewers',
    'select-career-framework': 'pages.installation-guide.tasks.descriptions.select-career-framework',
    'generate-first-levels': 'pages.installation-guide.tasks.descriptions.generate-first-levels',
    'review-publish-levels': 'pages.installation-guide.tasks.descriptions.review-publish-levels',
    'configure-skill-settings': 'pages.installation-guide.tasks.descriptions.configure-skill-settings',
    'review-publish-skills': 'pages.installation-guide.tasks.descriptions.review-publish-skills',
};

export const taskIdToCtaKey: Record<TaskId, TranslationKey> = {
    'define-purpose': 'pages.installation-guide.tasks.ctas.define-purpose',
    'company-information': 'pages.installation-guide.tasks.ctas.company-information',
    'data-upload': 'pages.installation-guide.tasks.ctas.data-upload',
    'build-foundation': 'pages.installation-guide.tasks.ctas.build-foundation',
    'review-families-clusters': 'pages.installation-guide.tasks.ctas.review-families-clusters',
    'create-job-profile-template': 'pages.installation-guide.tasks.ctas.create-job-profile-template',
    'generate-first-roles': 'pages.installation-guide.tasks.ctas.generate-first-roles',
    'review-publish-roles': 'pages.installation-guide.tasks.ctas.review-publish-roles',
    'invite-reviewers': 'pages.installation-guide.tasks.ctas.invite-reviewers',
    'select-career-framework': 'pages.installation-guide.tasks.ctas.select-career-framework',
    'generate-first-levels': 'pages.installation-guide.tasks.ctas.generate-first-levels',
    'review-publish-levels': 'pages.installation-guide.tasks.ctas.review-publish-levels',
    'configure-skill-settings': 'pages.installation-guide.tasks.ctas.configure-skill-settings',
    'review-publish-skills': 'pages.installation-guide.tasks.ctas.review-publish-skills',
};

export type StepId = 'setup' | 'structure' | 'roles' | 'leveling' | 'skills';

export const stepIdToTitleKey: Record<StepId, TranslationKey> = {
    setup: 'pages.installation-guide.steps.setup',
    structure: 'pages.installation-guide.steps.structure',
    roles: 'pages.installation-guide.steps.roles',
    leveling: 'pages.installation-guide.steps.leveling',
    skills: 'pages.installation-guide.steps.skills',
};

export const stepIdToSubtitleKey: Record<StepId, TranslationKey> = {
    setup: 'pages.installation-guide.steps.subtitles.setup',
    structure: 'pages.installation-guide.steps.subtitles.structure',
    roles: 'pages.installation-guide.steps.subtitles.roles',
    leveling: 'pages.installation-guide.steps.subtitles.leveling',
    skills: 'pages.installation-guide.steps.subtitles.skills',
};

export const SETUP_TASK_IDS = ['define-purpose', 'company-information', 'data-upload'] as const;

export type SetupTaskId = (typeof SETUP_TASK_IDS)[number];

type StepDefinition = {
    readonly id: StepId;
    readonly tasks: readonly TaskId[];
};

export const STEPS: readonly StepDefinition[] = [
    { id: 'setup', tasks: SETUP_TASK_IDS },
    {
        id: 'structure',
        tasks: ['build-foundation', 'review-families-clusters', 'create-job-profile-template'],
    },
    { id: 'roles', tasks: ['generate-first-roles', 'review-publish-roles', 'invite-reviewers'] },
    {
        id: 'leveling',
        tasks: ['select-career-framework', 'generate-first-levels', 'review-publish-levels'],
    },
    { id: 'skills', tasks: ['configure-skill-settings', 'review-publish-skills'] },
];

export const TOTAL_TASKS = STEPS.reduce((total, step) => total + step.tasks.length, 0);

const taskIdSet: ReadonlySet<string> = new Set(TASK_IDS);

export const isTaskId = (value: string): value is TaskId => taskIdSet.has(value);

export type SetupTaskCompletionState = Record<SetupTaskId, boolean>;

const setupTaskIdSet: ReadonlySet<TaskId> = new Set(SETUP_TASK_IDS);

export const isSetupTaskId = (taskId: TaskId): taskId is SetupTaskId => setupTaskIdSet.has(taskId);

export const isSetupTaskOpenable = (
    taskId: SetupTaskId,
    completion: SetupTaskCompletionState,
    isCompletionLoading: boolean,
): boolean => {
    if (isCompletionLoading) {
        return taskId === 'define-purpose';
    }

    if (taskId === 'company-information') {
        return completion['define-purpose'];
    }

    if (taskId === 'data-upload') {
        return completion['define-purpose'] && completion['company-information'];
    }

    return true;
};

export const URL_TASK_PARAM = 'task';
